/**
 * Fetcher
 *
 * Base Fetcher class.
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import get from 'lodash-es/get';
import isObject from 'lodash-es/isObject';
import config from '../config';
import Paginator from '../Paginator';
import { EventEmitterService } from '../services/event-emitter-provider';
import { ApiError, RequestAbortedError } from './api-error';
import { debounce } from 'lodash';

const DEFAULT_HEADERS = {
    Accept: 'application/json',
};

export class Fetcher {
    protected apiUrl: string;

    protected _registeredEvents: any[];
    protected cache: ng.ICacheObject;

    constructor(
        protected $cacheFactory: ng.ICacheFactoryService,
        protected $eventEmitter: EventEmitterService,
        protected $http: ng.IHttpService,
        protected $httpParamSerializer: ng.IHttpParamSerializer,
        protected $filter: ng.IFilterService,
        protected $log: ng.ILogService,
        protected $q: ng.IQService,
        protected $injector
    ) {
        this.apiUrl = config.apiUrl.replace(/\/+$/, '');

        this.$http = $http;
        this.$httpParamSerializer = $httpParamSerializer;
        this.$log = $log;
        this.$filter = $filter;
        this.$q = $q;
        this.$injector = $injector;

        this.cache = $cacheFactory(`http_${this.resourceName}`);

        this._registeredEvents = [];

        this._registerEventType('create');
        this._registerEventType('delete');
        this._registerEventType('update');
        this._registerEventType('patch');
        this._registerEventType('create_or_update');

        this._transformError = this._transformError.bind(this);

        this.init();
    }

    static get $inject() {
        return [
            '$cacheFactory',
            '$eventEmitter',
            '$http',
            '$httpParamSerializer',
            '$filter',
            '$log',
            '$q',
            '$injector',
        ];
    }

    get modelName(): string {
        throw ReferenceError('`get modelName()` must be overridden');
    }

    get resourceName(): string {
        throw ReferenceError('`get resourceName()` must be overridden');
    }

    /**
     * @param {string} type
     * @param {boolean} success
     * @param {boolean} both
     * @param {boolean} global
     * @protected
     */
    _registerEventType(type, { success = true, error = true, global = true } = {}) {
        if (success) {
            const eventName = this.eventName(type, success);

            this.$eventEmitter.registerEventType(eventName, {
                global,
            });

            this._registeredEvents.push(eventName);
        }

        if (error) {
            const eventName = this.eventName(type, false);

            this.$eventEmitter.registerEventType(eventName, {
                global,
            });

            this._registeredEvents.push(eventName);
        }
    }

    /**
     * @return {string[]}
     */
    get registeredEvents() {
        return [...this._registeredEvents];
    }

    /**
     * @return {string[]}
     */
    get registeredSuccessEvents() {
        return this._registeredEvents.filter(event => event.slice(-8) === '_success');
    }

    /**
     * @param {string} type
     * @param {*} data
     * @param {boolean} success
     * @protected
     */
    _broadcast(type, data, { success = true } = {}) {
        this.$eventEmitter.broadcast(this.eventName(type, success), {
            model: this.modelName,
            resource: this.resourceName,
            id: get(data, 'data.id', get(data, 'id')),
            type,
            data,
        });
    }

    _debounceBroadcast = debounce(this._broadcast.bind(this), 1000);

    /**
     * @param {string} type
     * @param {*} data
     * @protected
     */
    _broadcastError(type, data) {
        return this._broadcast(type, data, { success: false });
    }

    /**
     * Used for any initialisation without overriding the constructor
     * which requires injection.
     */
    init() {
        // Stub
    }

    /**
     * @param {string} type
     * @param {boolean} success
     * @return {string}
     */
    eventName(type, success = true) {
        return `${this.resourceName}:${type}_${success ? 'success' : 'error'}`;
    }

    _getUrl(key?: string): string {
        return `${this.apiUrl}/${this.resourceName}` + (key ? `/${key}` : '');
    }

    _delete(url, body, options: any = {}): angular.IPromise<any> {
        const headers = get(options, 'headers');
        const timeout = get(options, 'timeout');

        this._transformOptions(options);

        return this.$http
            .delete(url, {
                cache: false,
                timeout,
                headers: _buildHeaders(headers),
                data: body,
                params: options,
            })
            .then(response => response.data)
            .catch(this._transformError);
    }

    _get(url, options: any = {}): angular.IPromise<any> {
        const headers = get(options, 'headers');
        const timeout = get(options, 'timeout');

        return this.$http
            .get(url, {
                cache: options.cache ? this.cache : false,
                timeout,
                headers: _buildHeaders(headers),
                params: this._transformOptions(options),
            })
            .then(response => response.data)
            .catch(this._transformError);
    }

    _post(url, body, options?): angular.IPromise<any> {
        const headers = _buildHeaders(get(options, 'headers'));
        const timeout = get(options, 'timeout');

        this._transformOptions(options);

        if (body instanceof FormData) {
            headers['Content-Type'] = undefined;
        }

        return this.$http
            .post(url, body, {
                cache: false,
                timeout,
                headers,
                params: options,
            })
            .then(response => response.data)
            .catch(this._transformError);
    }

    _patch(url, body, options): angular.IPromise<any> {
        const headers = get(options, 'headers');
        const timeout = get(options, 'timeout');

        this._transformOptions(options);

        return this.$http
            .patch(url, body, {
                cache: false,
                timeout,
                headers: _buildHeaders(headers),
                params: options,
            })
            .then(response => response.data)
            .catch(this._transformError);
    }

    _put(url, body, options?): angular.IPromise<any> {
        const headers = _buildHeaders(get(options, 'headers'));
        const timeout = get(options, 'timeout');

        this._transformOptions(options);

        if (body instanceof FormData) {
            headers['Content-Type'] = undefined;
        }

        return this.$http
            .put(url, body, {
                cache: false,
                timeout,
                headers,
                params: options,
            })
            .then(response => response.data)
            .catch(this._transformError);
    }

    _transformOptions(options) {
        if (isObject(options)) {
            // Convert to CSV if array
            Object.keys(options).forEach(key => {
                const value = options[key];

                if (Array.isArray(value)) {
                    options[key] = value.join(',');
                }

                if (value instanceof Set) {
                    options[key] = Array.from(value);
                }
            });

            delete options.headers;
            delete options.timeout;
            delete options.cache;
        }

        return options;
    }

    all(params: any = {}) {
        params.all = true;

        return this._get(`${this.apiUrl}/${this.resourceName}`, params);
    }

    count(params = {}) {
        return this._get(this._getUrl('count'), params);
    }

    clearCache() {
        if (this.cache) {
            this.cache.removeAll();
        }
    }

    create(body, params) {
        return this._post(this._getUrl(), body, params).then(
            data => {
                this.clearCache();

                this._broadcast('create', data);

                return data;
            },
            error => {
                this._broadcastError('create', error);

                return this.$q.reject(error);
            },
        );
    }

    delete(key, body, params = {}) {
        if (!key) {
            return this.$q.reject('Missing key');
        }

        return this._delete(this._getUrl(key), body, params).then(
            data => {
                this.clearCache();

                this._broadcast('delete', {
                    ...data,
                    id: key,
                });

                return data;
            },
            error => {
                this._broadcastError('delete', error);

                return this.$q.reject(error);
            },
        );
    }

    get(key, options: any = {}): angular.IPromise<any> {
        if (!key) {
            return this.$q.reject('Missing key');
        }

        return this._get(this._getUrl(key), options);
    }

    paginate(options) {
        return this._get(this._getUrl(), options);
    }

    /**
     * @param {Object} config
     * @return {Paginator}
     */
    paginator(config = {}) {
        return this.$injector.instantiate(Paginator, {
            fetcher: this,
            config,
        });
    }

    createOrUpdate(body, params) {
        return this._put(this._getUrl(), body, params).then(
            data => {
                this.clearCache();

                this._broadcast('create_or_update', data);

                return data;
            },
            error => {
                this._broadcastError('create_or_update', error);

                return this.$q.reject(error);
            },
        );
    }

    update(key: string, body: any, params?: any, method = '_put') {
        if (!key) {
            return this.$q.reject('Missing key');
        }

        return this[method](this._getUrl(key), body, params).then(
            data => {
                this.clearCache();

                this._broadcast('update', data);

                return data;
            },
            error => {
                this._broadcastError('update', error);

                return this.$q.reject(error);
            },
        );
    }

    patch(key, body, params) {
        if (!key) {
            return this.$q.reject('Missing key');
        }

        return this._patch(this._getUrl(key), body, params).then(
            data => {
                this.clearCache();

                this._broadcast('patch', data);

                return data;
            },
            error => {
                this._broadcastError('patch', error);

                return this.$q.reject(error);
            },
        );
    }

    _transformError(response) {
        const { data, config, xhrStatus } = response;

        if (xhrStatus === 'abort') {
            return this.$q.reject(new RequestAbortedError(config.url));
        }

        this.$log.error('Request failed', response);

        if (data && data.error) {
            return this.$q.reject(new ApiError(data.error, config.url));
        }

        return this.$q.reject(response);
    }
}

export function _buildHeaders(headers = {}) {
    if (headers) {
        return {
            ...DEFAULT_HEADERS,
            ...headers,
        };
    }

    return {
        ...DEFAULT_HEADERS,
    };
}

export default Fetcher;
